// services/cashSessionService.js
import apiCall from './api';

/**
 * Service pour la gestion des sessions de caisse
 * Module Restaurant : ouverture/fermeture obligatoire avant les ventes
 */
export const cashSessionService = {

  /**
   * Récupère la session courante du vendeur connecté.
   * Retourne data: null s'il n'y a pas de session ouverte.
   */
  getCurrentSession: async () => {
    try {
      console.log('🔍 Récupération session courante...');
      const response = await apiCall.get('/employee/cash-sessions/current');

      if (response.success) {
        if (response.data) {
          console.log('✅ Session active:', response.data.session_number);
        } else {
          console.log('ℹ️ Aucune session ouverte');
        }
        return response;
      }

      return { success: false, data: null, message: response.message };
    } catch (error) {
      console.error('❌ Erreur récupération session courante:', error);
      return { success: false, data: null, message: 'Erreur de connexion' };
    }
  },

  /**
   * Ouvre une nouvelle session
   */
  openSession: async (openingAmount, openingNote = null) => {
    try {
      console.log('🔓 Ouverture session avec montant:', openingAmount);

      const payload = {
        opening_amount: parseFloat(openingAmount),
        opening_note: openingNote || null
      };

      const response = await apiCall.post('/employee/cash-sessions/open', payload);

      if (response.success) {
        console.log('✅ Session ouverte:', response.data?.session_number);
      }
      return response;
    } catch (error) {
      console.error('❌ Erreur ouverture session:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Erreur de connexion'
      };
    }
  },

  /**
   * Ferme une session (vendeur)
   */
  closeSession: async (sessionId, closingAmountReal, closingNote = null) => {
    try {
      console.log('🔒 Fermeture session:', sessionId);

      const payload = {
        closing_amount_real: parseFloat(closingAmountReal),
        closing_note: closingNote || null
      };

      const response = await apiCall.post(
        `/employee/cash-sessions/${sessionId}/close`,
        payload
      );

      if (response.success) {
        console.log('✅ Session fermée. Écart:', response.data?.closing_difference);
      }
      return response;
    } catch (error) {
      console.error('❌ Erreur fermeture session:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Erreur de connexion'
      };
    }
  },

  /**
   * Récupère le détail d'une session
   */
  getSessionById: async (sessionId) => {
    try {
      const response = await apiCall.get(`/employee/cash-sessions/${sessionId}`);
      return response;
    } catch (error) {
      console.error('❌ Erreur récupération session:', error);
      return { success: false, data: null, message: 'Erreur de connexion' };
    }
  },

  /**
   * ✅ Ventes rattachées à une session
   */
  getSessionSales: async (sessionId) => {
    try {
      const response = await apiCall.get(`/employee/cash-sessions/${sessionId}/sales`);
      return response;
    } catch (error) {
      console.error('❌ Erreur ventes session:', error);
      return { success: false, data: [], message: 'Erreur de connexion' };
    }
  },

  /**
   * ✅ Dépenses rattachées à une session
   */
  getSessionExpenses: async (sessionId) => {
    try {
      const response = await apiCall.get(`/employee/cash-sessions/${sessionId}/expenses`);
      return response;
    } catch (error) {
      console.error('❌ Erreur dépenses session:', error);
      return { success: false, data: [], message: 'Erreur de connexion' };
    }
  },

  // ============================================
  // 👨‍💼 ENDPOINTS MANAGER
  // ============================================

  /**
   * Historique des sessions (manager)
   * @param {Object} filters { status, userId, page, size }
   */
  getHistory: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.userId) params.append('userId', filters.userId);
      params.append('page', filters.page || 0);
      params.append('size', filters.size || 20);

      const response = await apiCall.get(`/manager/cash-sessions?${params}`);
      return response;
    } catch (error) {
      console.error('❌ Erreur historique sessions:', error);
      return { success: false, data: [], message: 'Erreur de connexion' };
    }
  },

  /**
   * Sessions actives (OPEN ou LOCKED) - manager
   */
  getActiveSessions: async () => {
    try {
      const response = await apiCall.get('/manager/cash-sessions/active');
      return response;
    } catch (error) {
      console.error('❌ Erreur sessions actives:', error);
      return { success: false, data: [], message: 'Erreur de connexion' };
    }
  },

  /**
   * Force la fermeture d'une session verrouillée (manager)
   */
  forceCloseSession: async (sessionId, closingAmountReal, closingNote) => {
    try {
      const payload = {
        closing_amount_real: parseFloat(closingAmountReal),
        closing_note: closingNote
      };
      const response = await apiCall.post(
        `/manager/cash-sessions/${sessionId}/force-close`,
        payload
      );
      return response;
    } catch (error) {
      console.error('❌ Erreur force-close:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Erreur de connexion'
      };
    }
  },

  // ============================================
  // 🛠️ UTILITAIRES
  // ============================================

  formatCurrency: (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0
    }).format(amount || 0);
  },

  formatDateTime: (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  },

  formatDuration: (minutes) => {
    if (!minutes) return '-';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins} min`;
    return `${hours}h ${mins.toString().padStart(2, '0')}min`;
  },

  getStatusBadge: (status) => {
    const map = {
      'OPEN': { variant: 'success', label: 'Ouverte', icon: '🟢' },
      'LOCKED': { variant: 'warning', label: 'Verrouillée (24h)', icon: '🔒' },
      'CLOSED': { variant: 'secondary', label: 'Fermée', icon: '⚫' }
    };
    return map[status] || { variant: 'secondary', label: status, icon: '❓' };
  }
};

export default cashSessionService;